import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { MediaImportOption } from 'components/FileUploader/types';
import { MediaImportedArgs } from 'containers/MediaUploadModal/types';
import { hideModal, pushModal } from 'redux/modules/modal';
import { Dispatch } from 'redux/types';
import { DropZoneType } from 'utils/constants';

const DEFAULT_MEDIA_UPLOAD_CONTEXT = DropZoneType.VIDEO_TRANSCRIPTION;

export interface UseMediaImportConfig {
  onMediaAccepted?: (args: MediaImportedArgs) => void;
}

export interface UseMediaImportResult {
  onMediaImport: (mediaImportOption: MediaImportOption) => Promise<void>;
  onYouTubeMediaImport: () => Promise<void>;
}

export default function useMediaImport({
  onMediaAccepted,
}: UseMediaImportConfig): UseMediaImportResult {
  const dispatch = useDispatch<Dispatch>();

  const onMediaImported = useCallback(
    (mediaImportedArgs: MediaImportedArgs): void => {
      dispatch(hideModal());
      onMediaAccepted(mediaImportedArgs);
    },
    [dispatch, onMediaAccepted],
  );

  const handleMediaImport = useCallback(
    async (mediaImportOption: MediaImportOption): Promise<void> => {
      const mediaImportedArgs: MediaImportedArgs = await dispatch(
        pushModal({
          name: 'MediaUpload',
          params: {
            mediaImportOption,
            uploadContext: DEFAULT_MEDIA_UPLOAD_CONTEXT,
          },
        }),
      );

      onMediaImported(mediaImportedArgs);
    },
    [dispatch, onMediaImported],
  );

  const handleYouTubeMediaImport = useCallback(async (): Promise<void> => {
    const youtubeMediaImportedArgs: MediaImportedArgs = await dispatch(
      pushModal({
        name: 'YouTubeMediaUpload',
        params: {
          uploadContext: DEFAULT_MEDIA_UPLOAD_CONTEXT,
        },
      }),
    );

    onMediaImported(youtubeMediaImportedArgs);
  }, [dispatch, onMediaImported]);

  return {
    onMediaImport: handleMediaImport,
    onYouTubeMediaImport: handleYouTubeMediaImport,
  };
}
